import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import moment from '@/common/modules/moment-fallback';
import type { Accommodation, AccommodationLocation, MadeBooking, PriceCalculation } from '@/widgets/Wizard/models/wizard';
import { useMetadataStore } from '@/widgets/Wizard/stores/metadata';

export const useReservationStore = defineStore('booking-wizard/reservation', () => {
    const metaDataStore = useMetadataStore();
    const { availableAccommodations: metaAvailableAccommodations } = storeToRefs(metaDataStore);

    // State
    const reservedAgeGroups = ref<{ [id: string]: number }>({});
    const reservedPets = ref<{ [id: string]: number }>({});
    const arrivalDate = ref<Date | null>(null);
    const totalNightsStaying = ref(0);
    const availableAccommodations = ref<Accommodation[] | null>(null);
    const chosenAccommodation = ref<Accommodation | null>(null);
    const chosenAccommodationLocation = ref<AccommodationLocation | null>(null);
    const chosenAccommodationExtras = ref<{ [id: string]: number }>({});
    const personalDetails = ref<{ [key: string]: string }>({});
    const priceCalculation = ref<PriceCalculation | null>(null);
    const booking = ref<MadeBooking | null>(null);
    const bookingFailed = ref<boolean>(false);
    const arrangementId = ref<string | null>(null);
    const arrangementLoadFailed = ref(false);

    // Getters
    const hasValidStayPeriod = computed<boolean>(() => {
        if (!arrivalDate.value || !departureDate.value) {
            return false;
        }

        return availableAccommodations.value !== null && availableAccommodations.value.length > 0;
    });

    const departureDate = computed<Date | null>(() => {
        if (!arrivalDate.value || !(arrivalDate.value instanceof Date)) {
            return null;
        }

        return moment(arrivalDate.value).add(totalNightsStaying.value, 'days').getDateObject();
    });

    const hasValidBooking = computed<boolean>(() => {
        return !!priceCalculation.value && !!arrivalDate.value && !!departureDate.value && !!chosenAccommodation.value;
    });

    // Actions
    const reset = () => {
        reservedAgeGroups.value = {};
        reservedPets.value = {};
        arrivalDate.value = null;
        totalNightsStaying.value = 0;
        availableAccommodations.value = null;
        chosenAccommodation.value = null;
        chosenAccommodationLocation.value = null;
        chosenAccommodationExtras.value = {};
        personalDetails.value = {};
        priceCalculation.value = null;
        booking.value = null;
        bookingFailed.value = false;
        arrangementId.value = null;
        arrangementLoadFailed.value = false;
    };

    // Watchers
    watch(chosenAccommodation, () => {
        if (
            !chosenAccommodation.value ||
            !chosenAccommodationLocation.value ||
            !chosenAccommodationLocation.value.accommodation
        ) {
            return;
        }

        if (chosenAccommodation.value.id !== chosenAccommodationLocation.value.accommodation.id) {
            chosenAccommodationLocation.value = null;
        }
    });

    watch(reservedAgeGroups, () => resetAvailableAccommodations(), { deep: true });
    watch(reservedPets, () => resetAvailableAccommodations(), { deep: true });

    const resetAvailableAccommodations = () => {
        if (metaAvailableAccommodations) {
            metaAvailableAccommodations.value = null;
        }

        availableAccommodations.value = null;
    };

    return {
        // State
        reservedAgeGroups,
        reservedPets,
        arrivalDate,
        totalNightsStaying,
        availableAccommodations,
        chosenAccommodation,
        chosenAccommodationLocation,
        chosenAccommodationExtras,
        personalDetails,
        priceCalculation,
        booking,
        bookingFailed,
        arrangementId,
        arrangementLoadFailed,

        // Getters
        hasValidStayPeriod,
        departureDate,
        hasValidBooking,

        // Actions
        reset,
    };
});
